import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { AppInstalledView } from '@hero/api';
import type { WaybillTrigger } from '@hero/schema';
import {
  useAppsApi,
  useAppLogistiInstallApi,
  useAppLogistiUninstallApi,
  useAppLogistiSettingsApi,
  useOrgProfileApi,
  useOrgProfileUpdateApi,
} from '@hero/api-hooks';
import { useCurrentUser } from '@hero/hooks-shared';
import { ModalForm } from '../../../app/Modal';
import { AppConnectBrand } from '../AppConnectBrand';
import { SelectAuto, type SelectAutoOption } from '../../../app/Select';
import { Input } from '../../../ui/Input/Input';
import { Switch } from '../../../ui/Switch';
import { Button } from '../../../ui/Button';
import { LOGISTI_APP, isBayanEstablishmentNumber } from '@hero/apps';

export interface LogistiConnectModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The tenant's existing row, enabled or not. */
  install?: AppInstalledView;
}

const TRIGGERS: WaybillTrigger[] = ['on_first_pickup', 'on_driver_assign', 'manual_only'];

/**
 * Turn government waybills on, or reconfigure how they are filed.
 *
 * ## Three ways to be on, and the shortest one is the default
 *
 * Most tenants never need a Bayan account of their own: documents are filed on
 * Hero's licence and their own CR is what the وثيقة declares as the carrier.
 * For them this is one field. Getting a `client_id` from ELM takes about a
 * week, so leading with it would put a week between signing up and the first
 * document — which is why the client ID sits behind an explicit "I want my own
 * account" disclosure rather than at the top of the form.
 *
 * A freelance driver is the third shape: Bayan files them as COMPANY with
 * هوية صاحب العمل as `carrier.moi`, so they need that unified number (starts
 * with 7) plus the ID number and version printed on the card.
 *
 * ## Why the settings block is no longer own-account only
 *
 * It used to be, because settings live on the install row and platform filing
 * wrote none. Every tenant now has exactly one row whatever its filing mode, so
 * the trigger a platform tenant chooses here is finally the trigger that fires.
 *
 * ## Why one modal for connect and settings
 *
 * The fields matter the same either way: which licence, and when to issue.
 * Splitting them would mean a tenant that mistyped its client ID has to
 * disconnect before it can correct it. Here the client ID is simply optional
 * once connected — leave it blank to keep the stored credential, or type a new
 * one to replace it.
 */
export const LogistiConnectModal: React.FC<LogistiConnectModalProps> = ({
  isOpen,
  onClose,
  install,
}) => {
  const { t } = useTranslation(['m-apps', 'c-main']);
  const { isFreelancerDriver, driver } = useCurrentUser();

  // A row that exists but is switched off. Worth distinguishing from never
  // having connected: nothing was deleted, so this is a one-click resume and
  // the copy should not ask for details that are already on file.
  const hasRow = Boolean(install);
  const isConnected = Boolean(install?.enabled);
  const isTurnedOff = hasRow && !isConnected;

  // The individual path follows the account, not the row: a freelancer opening
  // this for the first time has no row to read a mode off, and is exactly the
  // person who must not be shown a CR field.
  const isIndividual = isFreelancerDriver || install?.filingMode === 'individual';

  // Errors for an org-less caller, so the freelancer path must not ask.
  const { data: profile } = useOrgProfileApi({ enabled: !isIndividual });
  const storedCrNumber = isIndividual
    ? (driver?.legal?.employerIdentity ?? driver?.legal?.registrationId ?? '')
    : (profile?.registrationId ?? '');

  // Whether Hero can file on this tenant's behalf at all. Without a platform
  // licence configured on this backend, the tenant's own `client_id` is the
  // only way through, so the disclosure stops being optional.
  const { data: apps } = useAppsApi();
  const hasPlatformAccount = Boolean(apps?.find(app => app.id === LOGISTI_APP.id)?.platformAccount);

  // Always on once they have a row carrying credentials — that is the account
  // they came here to manage — and otherwise only when Hero has no licence to
  // file on, which is the one case where a tenant's own `client_id` is the only
  // way through. The opt-in disclosure that used to widen this is commented out
  // below: a `client_id` is a week of ELM paperwork for something almost nobody
  // needs, and offering it up front had people choosing it by mistake.
  const hasOwnAccount = install?.filingMode === 'own_account';
  const showOwnAccount = !isIndividual && (hasOwnAccount || !hasPlatformAccount);

  const [accountId, setAccountId] = useState('');
  const [trigger, setTrigger] = useState<WaybillTrigger>('on_first_pickup');
  const [autoCloseOnDelivery, setAutoCloseOnDelivery] = useState(true);
  const [autoCancelOnOrderCancel, setAutoCancelOnOrderCancel] = useState(true);

  // Not editable here any more — a default cargo type is a detail the mapper
  // only reaches for when a load's own type has no match in Bayan's list, and
  // it was one more decision on a screen whose job is to get someone issuing.
  // Still sent on every save: the backend merges settings, so leaving the key
  // out as `undefined` would reset a value picked elsewhere.
  const defaultGoodTypeId = install?.settings?.defaultGoodTypeId;

  // `null` means untouched, so the field tracks the stored CR until the user
  // edits it. Seeding into state instead would either show blank when the
  // profile query resolves after the modal opens, or need `storedCrNumber` in
  // the reset effect below — which would then wipe whatever had been typed
  // the moment the query landed.
  const [crEdit, setCrEdit] = useState<string | null>(null);
  const crNumber = crEdit ?? storedCrNumber;

  // Same `null`-means-untouched treatment as the CR, and for the same reason:
  // the Driver doc arrives with the `me` payload, which may resolve after the
  // modal is already open.
  const [idEdit, setIdEdit] = useState<string | null>(null);
  const [idIssueEdit, setIdIssueEdit] = useState<string | null>(null);
  const driverIdNumber = idEdit ?? driver?.legal?.id ?? '';
  const driverIdIssueNumber = idIssueEdit ?? String(driver?.legal?.idIssueNumber ?? '');

  // Reseed whenever the modal opens so a cancelled edit doesn't persist into
  // the next open, and so a settings change made elsewhere is picked up.
  useEffect(() => {
    if (!isOpen) return;
    setAccountId('');
    setCrEdit(null);
    setIdEdit(null);
    setIdIssueEdit(null);
    setTrigger(install?.settings?.trigger ?? 'on_first_pickup');
    setAutoCloseOnDelivery(install?.settings?.autoCloseOnDelivery ?? true);
    setAutoCancelOnOrderCancel(install?.settings?.autoCancelOnOrderCancel ?? true);
  }, [isOpen, install]);

  const settings = {
    trigger,
    autoCloseOnDelivery,
    autoCancelOnOrderCancel,
    defaultGoodTypeId,
  };

  const triggerOptions: SelectAutoOption[] = TRIGGERS.map(value => ({
    value,
    label: t(`m-apps:form.trigger.options.${value}`),
  }));

  const connect = useAppLogistiInstallApi({
    onSuccess: () => {
      toast.success(
        showOwnAccount && accountId.trim()
          ? t('m-apps:toast.success.connected')
          : t('m-apps:toast.success.activated'),
      );
      onClose();
    },
    // On the own-account path the backend verifies the client ID with Bayan
    // before saving anything and refuses the install if it can't, so an error
    // here is Bayan's own verdict — more useful than a generic string.
    onError: (error: Error) => toast.error(error.message || t('m-apps:toast.error.connect_failed')),
  });

  const saveSettings = useAppLogistiSettingsApi({
    onSuccess: () => {
      toast.success(t('m-apps:toast.success.settings_saved'));
      onClose();
    },
    onError: () => toast.error(t('m-apps:toast.error.settings_failed')),
  });

  const disconnect = useAppLogistiUninstallApi({
    onSuccess: () => {
      toast.success(t('m-apps:toast.success.disconnected'));
      onClose();
    },
    onError: () => toast.error(t('m-apps:toast.error.disconnect_failed')),
  });

  const updateProfile = useOrgProfileUpdateApi({
    // The CR is what a platform row files under, so writing it changes an
    // answer that lives under the `app` entity, not `org`. Without this the
    // order actions menu keeps offering "Enable Logisti" to someone who just
    // enabled it.
    invalidateQueryKeys: [['app']],
    onError: () => toast.error(t('m-apps:toast.error.cr_number_failed')),
  });

  const handleSave = async () => {
    const trimmedAccountId = accountId.trim();
    const trimmedCr = crNumber.trim();

    // A new or replacement credential has to go through connect so Bayan
    // verifies it. This is also the platform-to-own-account upgrade: the
    // backend flips the existing row's mode rather than refusing it.
    if (showOwnAccount && trimmedAccountId) {
      connect.mutate({
        filingMode: 'own_account',
        accountId: trimmedAccountId,
        crNumber: trimmedCr,
        settings,
      });
      return;
    }

    // Settings-only on a verified own account. Connect is not an option here —
    // it would need the client ID retyped just to change a toggle — so the CR
    // edit goes to the profile and the toggles to the settings route.
    if (isConnected && hasOwnAccount) {
      if (trimmedCr && trimmedCr !== storedCrNumber) {
        try {
          await updateProfile.mutateAsync({ legal: { registrationId: trimmedCr } });
        } catch {
          return;
        }
      }
      saveSettings.mutate(settings);
      return;
    }

    // Filing on Hero's licence: one idempotent call that creates the row,
    // re-enables a switched-off one, stores the identity the وثيقة will
    // declare — the org's CR, or the driver's card — and saves the settings.
    // It used to be a profile patch and nothing else, which is why enabling
    // over WhatsApp or here left the integrations page showing "not connected".
    connect.mutate({
      filingMode: isIndividual ? 'individual' : 'platform',
      crNumber: trimmedCr,
      ...(isIndividual
        ? {
            driverIdentity: {
              id: driverIdNumber.trim(),
              idIssueNumber: Number(driverIdIssueNumber.trim()),
            },
          }
        : {}),
      settings,
    });
  };

  const isSaving = connect.isPending || saveSettings.isPending || updateProfile.isPending;

  // The identity each path files under has to be present before Save does
  // anything: the client ID authenticates to Bayan, the CR is stamped on every
  // waybill an org issues, and a freelancer's ID number plus the version number
  // on the card is what names him as the carrier.
  //
  // Keyed on whether a credential is already stored rather than on `enabled`,
  // so switching a connector back on does not demand the client ID again.
  // Disconnect keeps it, the placeholder shows it masked, and re-enabling is a
  // one-click resume.
  const hasStoredAccountId = Boolean(install?.accountIdMasked);
  const hasCr = isBayanEstablishmentNumber(crNumber);
  const hasDriverIdentity =
    driverIdNumber.trim().length > 0 && Number(driverIdIssueNumber.trim()) > 0;
  const canSave = isIndividual
    ? hasDriverIdentity && hasCr
    : showOwnAccount && !hasStoredAccountId
    ? hasCr && accountId.trim().length > 0
    : hasCr;

  const title = isConnected
    ? t('m-apps:modal.settings_title')
    : isTurnedOff
    ? t('m-apps:modal.reconnect_title')
    : isIndividual
    ? t('m-apps:modal.individual_title')
    : showOwnAccount
    ? t('m-apps:modal.connect_title')
    : t('m-apps:modal.activate_title');

  const description = isConnected
    ? undefined
    : isTurnedOff
    ? t('m-apps:modal.reconnect_description')
    : isIndividual
    ? t('m-apps:modal.individual_description')
    : showOwnAccount
    ? t('m-apps:modal.connect_description')
    : t('m-apps:modal.activate_description');

  return (
    <ModalForm
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      description={description}
      headerBrand={
        <AppConnectBrand
          name={t('m-apps:logisti.name')}
          logo={LOGISTI_APP.logo}
          label={isConnected ? undefined : LOGISTI_APP.label}
          subtitle={
            isConnected
              ? t(`m-apps:logisti.filing.${install?.filingMode ?? 'platform'}`)
              : isTurnedOff
              ? t('m-apps:logisti.turned_off')
              : t('m-apps:logisti.disconnected')
          }
        />
      }
      onSave={handleSave}
      isLoading={isSaving}
      disableSave={!canSave}
      saveText={
        isConnected
          ? t('c-main:crud_buttons.save')
          : isTurnedOff
          ? t('m-apps:logisti.reconnect')
          : t('m-apps:logisti.connect')
      }
      size="md">
      <div className="space-y-5">
        {showOwnAccount ? (
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-900" htmlFor="logisti-client-id">
              {t('m-apps:form.client_id.title')}
            </label>
            <Input
              id="logisti-client-id"
              value={accountId}
              onChange={event => setAccountId(event.target.value)}
              placeholder={install?.accountIdMasked ?? t('m-apps:form.client_id.placeholder')}
              inputMode="numeric"
              autoComplete="off"
            />
            <p className="text-xs text-gray-500">{t('m-apps:form.client_id.description')}</p>
          </div>
        ) : null}

        {isIndividual ? (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-900" htmlFor="logisti-driver-id">
                {t('m-apps:form.driver_identity.title')}
              </label>
              <Input
                id="logisti-driver-id"
                value={driverIdNumber}
                onChange={event => setIdEdit(event.target.value)}
                placeholder={t('m-apps:form.driver_identity.placeholder')}
                inputMode="numeric"
                autoComplete="off"
              />
              <p className="text-xs text-gray-500">
                {t('m-apps:form.driver_identity.description')}
              </p>
            </div>

            <div className="space-y-1.5">
              <label
                className="text-sm font-medium text-gray-900"
                htmlFor="logisti-driver-id-issue">
                {t('m-apps:form.driver_id_issue_number.title')}
              </label>
              <Input
                id="logisti-driver-id-issue"
                value={driverIdIssueNumber}
                onChange={event => setIdIssueEdit(event.target.value)}
                placeholder={t('m-apps:form.driver_id_issue_number.placeholder')}
                inputMode="numeric"
                autoComplete="off"
              />
              <p className="text-xs text-gray-500">
                {t('m-apps:form.driver_id_issue_number.description')}
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-900" htmlFor="logisti-employer-cr">
                {t('m-apps:form.employer_cr.title')}
              </label>
              <Input
                id="logisti-employer-cr"
                value={crNumber}
                onChange={event => setCrEdit(event.target.value)}
                placeholder={t('m-apps:form.employer_cr.placeholder')}
                inputMode="numeric"
                autoComplete="off"
              />
              <p className="text-xs text-gray-500">{t('m-apps:form.employer_cr.description')}</p>
            </div>
          </div>
        ) : (
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-900" htmlFor="logisti-cr-number">
              {t('m-apps:form.cr_number.title')}
            </label>
            <Input
              id="logisti-cr-number"
              value={crNumber}
              onChange={event => setCrEdit(event.target.value)}
              placeholder={t('m-apps:form.cr_number.placeholder')}
              inputMode="numeric"
              autoComplete="off"
            />
            <p className="text-xs text-gray-500">{t('m-apps:form.cr_number.description')}</p>
          </div>
        )}

        {/* The own-account opt-in, withdrawn for now.
            
            It was offered to anyone Hero could already file for, on the
            reasoning that someone who wants their own licence should be able to
            find it without contacting support. In practice it read as a choice
            between two equally valid setups, and people picked the one that
            takes ELM about a week — so the fastest path to a first document was
            being opted out of by mistake. Kept here rather than deleted because
            the intent stands: the tenants who genuinely want their own
            `client_id` go through support, and this is what comes back when
            there is a way to tell them apart. `showOwnAccount` no longer reads
            `wantsOwnAccount`, so restoring this needs that state back too.

        {!isConnected && !isIndividual && hasPlatformAccount ? (
          <div className="border-t border-gray-200 pt-4">
            {showOwnAccount ? (
              <>
                <p className="text-sm font-medium text-gray-900">{t('m-apps:own_account.title')}</p>
                <p className="mt-1 text-xs text-gray-500">{t('m-apps:own_account.description')}</p>
                <Button
                  variant="ghost"
                  size="md"
                  className="mt-2 px-0"
                  onClick={() => setWantsOwnAccount(false)}>
                  {t('m-apps:own_account.back')}
                </Button>
              </>
            ) : (
              <Button
                variant="ghost"
                size="md"
                className="px-0"
                onClick={() => setWantsOwnAccount(true)}>
                {t('m-apps:own_account.toggle')}
              </Button>
            )}
          </div>
        ) : null}
        */}

        <div className="space-y-1.5">
          <label className="text-sm font-medium text-gray-900">
            {t('m-apps:form.trigger.title')}
          </label>
          <SelectAuto
            options={triggerOptions}
            value={trigger}
            onChange={value => setTrigger(value as WaybillTrigger)}
          />
          <p className="text-xs text-gray-500">{t('m-apps:form.trigger.description')}</p>
        </div>

        <SettingToggle
          label={t('m-apps:form.auto_close_on_delivery.title')}
          description={t('m-apps:form.auto_close_on_delivery.description')}
          checked={autoCloseOnDelivery}
          onChange={setAutoCloseOnDelivery}
        />

        <SettingToggle
          label={t('m-apps:form.auto_cancel_on_order_cancel.title')}
          description={t('m-apps:form.auto_cancel_on_order_cancel.description')}
          checked={autoCancelOnOrderCancel}
          onChange={setAutoCancelOnOrderCancel}
        />

        {isConnected ? (
          <div className="border-t border-gray-200 pt-4">
            <Button
              variant="ghost"
              size="md"
              className="text-red-600 hover:bg-red-50 hover:text-red-700"
              disabled={disconnect.isPending}
              onClick={() => disconnect.mutate(undefined)}>
              {t('m-apps:modal.disconnect_title')}
            </Button>
            <p className="mt-1 text-xs text-gray-500">{t('m-apps:modal.disconnect_description')}</p>
          </div>
        ) : null}
      </div>
    </ModalForm>
  );
};

const SettingToggle: React.FC<{
  label: string;
  description: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}> = ({ label, description, checked, onChange }) => (
  <div className="flex items-start justify-between gap-4">
    <div className="flex-1">
      <p className="text-sm font-medium text-gray-900">{label}</p>
      <p className="text-xs text-gray-500">{description}</p>
    </div>
    <Switch checked={checked} onCheckedChange={onChange} />
  </div>
);
